import CryptoJS from 'crypto-js';

// Generate a consistent secret key based on device/session info
const COMMON_SECRET_KEY = "4JuTM8EFA2NOe4W3j6EYoPCaH1kWJusN0zIsLBO1Edk1SFvvTP";
const generateSecretKey = () => {
    // Create a consistent hash from device characteristics
    return CryptoJS.SHA256(COMMON_SECRET_KEY).toString();
};
// const generateSecretKey = () => {
//     const userAgent = navigator.userAgent;
//     const screenInfo = `${window.screen.width}x${window.screen.height}`;
//     const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
//     const base = `${userAgent}-${screenInfo}-${timezone}`;

//     // Create a consistent hash from device characteristics
//     return CryptoJS.SHA256(base).toString();
// };

// Get the secret key (cached)
let secretKey = null;
const getSecretKey = () => {
    if (!secretKey) {
        secretKey = generateSecretKey();
    }
    return secretKey;
};

/**
 * Encrypts data using AES encryption
 * @param {Object} data - The data to encrypt
 * @returns {string} - Encrypted string
 */
export const encryptData = (data) => {
    try {
        const key = getSecretKey();
        const jsonString = JSON.stringify(data);
        const encrypted = CryptoJS.AES.encrypt(jsonString, key).toString();
        return encrypted;
    } catch (error) {
        console.error('Encryption error:', error);
        // Fallback to unencrypted storage in case of error
        return JSON.stringify(data);
    }
};

/**
 * Decrypts data using AES decryption
 * @param {string} encryptedData - The encrypted data string
 * @returns {Object|null} - Decrypted data or null if failed
 */
export const decryptData = (encryptedData) => {
    if (!encryptedData) return null;

    try {
        // Check if the data looks like CryptoJS encrypted data (starts with "U2FsdGVkX1")
        const isEncrypted = encryptedData.startsWith('U2FsdGVkX1');

        if (!isEncrypted) {
            // If it doesn't look encrypted, try parsing as plain JSON
            try {
                return JSON.parse(encryptedData);
            } catch (jsonError) {
                console.warn('Data is not encrypted and not valid JSON:', jsonError);
                return null;
            }
        }

        // Try to decrypt the encrypted data
        const key = getSecretKey();
        try {
            const decryptedBytes = CryptoJS.AES.decrypt(encryptedData, key);
            const decryptedString = decryptedBytes.toString(CryptoJS.enc.Utf8);

            if (!decryptedString) {
                console.error('Decryption resulted in empty string');
                return null;
            }

            return JSON.parse(decryptedString);
        } catch (decryptError) {
            console.error('Failed to decrypt data:', decryptError);
            return null;
        }
    } catch (error) {
        console.error('Decryption error:', error);
        return null;
    }
};

/**
 * Securely stores data in localStorage with encryption
 * @param {string} key - Storage key
 * @param {Object} data - Data to store
 */
export const setSecureItem = (key, data) => {
    try {
        if (data === null || data === undefined) {
            localStorage.removeItem(key);
            return;
        }

        const encryptedData = encryptData(data);
        localStorage.setItem(key, encryptedData);
    } catch (error) {
        console.error('Secure storage error:', error);
        // Fallback to regular localStorage
        try {
            localStorage.setItem(key, JSON.stringify(data));
        } catch (fallbackError) {
            console.error('Fallback storage also failed:', fallbackError);
        }
    }
};

/**
 * Retrieves and decrypts data from localStorage
 * @param {string} key - Storage key
 * @returns {Object|null} - Decrypted data or null
 */
export const getSecureItem = (key) => {
    try {
        const storedData = localStorage.getItem(key);
        if (!storedData) return null;

        const decryptedData = decryptData(storedData);

        // If decryption fails, it might be corrupted encrypted data
        if (decryptedData === null && storedData.startsWith('U2FsdGVkX1')) {
            console.warn(`Corrupted encrypted data detected for key: ${key}. Clearing...`);
            localStorage.removeItem(key);
            return null;
        }

        return decryptedData;
    } catch (error) {
        console.error('Secure retrieval error:', error);
        // If there's an error and the data looks encrypted, clear it
        try {
            const storedData = localStorage.getItem(key);
            if (storedData && storedData.startsWith('U2FsdGVkX1')) {
                console.warn(`Clearing corrupted encrypted data for key: ${key}`);
                localStorage.removeItem(key);
            }
        } catch (clearError) {
            console.error('Failed to clear corrupted data:', clearError);
        }
        return null;
    }
};

/**
 * Removes item from secure storage
 * @param {string} key - Storage key
 */
export const removeSecureItem = (key) => {
    try {
        localStorage.removeItem(key);
    } catch (error) {
        console.error('Secure removal error:', error);
    }
};

/**
 * Migrates existing unencrypted cart data to encrypted storage
 * @param {string} key - Storage key
 */
export const migrateToSecureStorage = (key) => {
    try {
        const existingData = localStorage.getItem(key);
        if (!existingData) return;

        // Try to parse as JSON to see if it's unencrypted
        try {
            const parsedData = JSON.parse(existingData);
            // If parsing succeeds, it's unencrypted - encrypt it
            setSecureItem(key, parsedData);
        } catch (parseError) {
            // If parsing fails, assume it's already encrypted
        }
    } catch (error) {
        console.error('Migration error:', error);
    }
};